import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import { Mutex } from 'async-mutex';
import { API_URL } from '../constants';
import i18n from '../locales/i18n';
import { logout as logoutDispatch } from '../redux/slices/applicationSlice';
import { store } from '../redux/store';
import { trimObject } from '../utils/formatUtils';

const refreshMutex = new Mutex();

const httpService: AxiosInstance = axios.create({
  baseURL: API_URL,
});

function logout(): void {
  wipeToken();
  store.dispatch(logoutDispatch());
}

async function refreshTokenIfNeeded(): Promise<void> {
  let accessTokenExp = localStorage.getItem('accessTokenExp');
  if (!accessTokenExp) {
    // for already logged in users when we don't have accessTokenExp yet
    const accessToken = localStorage.getItem('token');
    if (accessToken) {
      try {
        const decoded = jwtDecode<JwtPayload>(accessToken);
        if (decoded.exp) {
          localStorage.setItem('accessTokenExp', (decoded.exp * 1000).toString());
        }
      } catch {
        logout();
      }
      accessTokenExp = localStorage.getItem('accessTokenExp');
    }
  }

  // If there is an access token in the store and it has already expired/will expire in the next 20 seconds
  if (accessTokenExp && Date.now() + 20000 >= +accessTokenExp) {
    // Either try to refresh the token or wait for another request to do it
    if (!refreshMutex.isLocked()) {
      const release = await refreshMutex.acquire();
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const refreshResult = await axios.post(`${API_URL}/auth/refresh`, { refreshToken });

          if (refreshResult.data) {
            setToken(refreshResult.data.accessToken);
            setRefreshToken(refreshResult.data.refreshToken);
          } else {
            logout();
          }
        } else {
          logout();
        }
      } catch {
        logout();
      } finally {
        release();
      }
    } else {
      await refreshMutex.waitForUnlock();
    }
  }
}

const attachToken = async (request: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
  await refreshTokenIfNeeded();
  const token = localStorage.getItem('token');
  request.headers.Authorization = token ? `Bearer ${token}` : '';
  return request;
};

export const attachVersion = (request: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  // eslint-disable-next-line global-require
  request.headers['App-Version'] = `web-${require('../../package.json').version}`;
  return request;
};

const trimBody = (request: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  if (request.data) {
    request.data = trimObject(request.data);
  }
  return request;
};

export function getUserId(): number {
  const token = localStorage.getItem('token') ?? '';
  const data = jwtDecode<JwtData>(token);
  return data.payload.userId;
}

httpService.interceptors.request.use(attachToken);
httpService.interceptors.request.use(attachVersion);
httpService.interceptors.request.use(trimBody);

// Enable detailed HTTP logging in development or when `localStorage.httpDebug` is set to 'true'
const isHttpDebug =
  process.env.NODE_ENV !== 'production' ||
  (() => {
    try {
      return localStorage.getItem('httpDebug') === 'true';
    } catch {
      return false;
    }
  })();

if (isHttpDebug) {
  httpService.interceptors.request.use((req) => {
    // Log method, url, params and body
    // eslint-disable-next-line no-console
    console.debug('[HTTP Request]', {
      method: req.method?.toUpperCase(),
      url: req.url,
      params: req.params,
      data: req.data,
      headers: req.headers,
    });
    return req;
  });

  httpService.interceptors.response.use(
    (res) => {
      // eslint-disable-next-line no-console
      console.debug('[HTTP Response]', {
        status: res.status,
        method: res.config?.method?.toUpperCase(),
        url: res.config?.url,
        data: res.data,
      });
      return res;
    },
    (error) => {
      // eslint-disable-next-line no-console
      console.error('[HTTP Error]', {
        message: error.message,
        config: {
          method: error.config?.method,
          url: error.config?.url,
          params: error.config?.params,
          data: error.config?.data,
        },
        status: error.response?.status,
        responseData: error.response?.data,
      });
      return Promise.reject(error);
    },
  );
}

export const handleUnAuthorizedError = async (error: unknown): Promise<any> => {
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 401) {
      const originalRequest = error.config;

      if (!originalRequest) return Promise.reject(error);
      await attachToken(originalRequest); // includes token refresh

      const accessToken = localStorage.getItem('token');
      if (!accessToken) return Promise.reject(error);

      try {
        return await httpService(originalRequest);
      } catch (retryError) {
        if (axios.isAxiosError(retryError) && retryError.response?.status === 401) {
          logout();
        }
        return Promise.reject(retryError);
      }
    }
  }
  return Promise.reject(error);
};

httpService.interceptors.response.use(null, handleUnAuthorizedError);

const handleError = (error: any): Promise<void> => {
  if (!error.response.data.print) return Promise.reject(error);
  const errorMessage = error.response.data.error;

  toast.error(errorMap[errorMessage as keyof typeof errorMap] || errorMessage);

  return Promise.reject(error);
};

httpService.interceptors.response.use(null, handleError);

export const hasToken = (): boolean => {
  return !!localStorage.getItem('token');
};
export const setToken = (token: string): void => {
  localStorage.setItem('token', token);
  const decoded = jwtDecode<JwtPayload>(token);
  if (decoded.exp) {
    localStorage.setItem('accessTokenExp', (decoded.exp * 1000).toString());
  }
};
export const setRefreshToken = (token: string): void => {
  localStorage.setItem('refreshToken', token);
};

export const wipeToken = (): void => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('accessTokenExp');
};

export const setHttpDebug = (enabled: boolean): void => {
  try {
    localStorage.setItem('httpDebug', enabled ? 'true' : 'false');
  } catch (e) {
    console.error('Failed to set httpDebug in localStorage', e);
  }
};

export default httpService;

const errorMap = {
  'Requested a new magic link too soon.': i18n.t('toast.server.linkTooSoon'),
  'There is no user with such primary email!': i18n.t('toast.server.inexistantUser'),
  'User already exists!': i18n.t('toast.server.userAlreadyExists'),
};
